import { ConfirmDialog } from '@/shared/components';
import { useTranslation } from 'react-i18next';
import type { UserAccessResponse, UserResponse } from '@/shared/api';
import { UserNameDialog } from '../UserNameDialog';
import { UserTwoFactorResetDialog } from '../UserTwoFactorResetDialog';
import { fullName } from '../userStatus';
import type { useUserMutations } from '../useUserMutations';
import { UserAccessDialog } from './UserAccessDialog';

export interface UserDialogState {
  name: boolean;
  access: boolean;
  twoFactorReset: boolean;
  deactivate: boolean;
}

export const CLOSED_USER_DIALOGS: UserDialogState = {
  name: false,
  access: false,
  twoFactorReset: false,
  deactivate: false,
};

interface Props {
  user: UserResponse;
  access: UserAccessResponse | null;
  state: UserDialogState;
  onChange: (patch: Partial<UserDialogState>) => void;
  mutations: ReturnType<typeof useUserMutations>;
}

/**
 * Die Overlays der Benutzerakte an einer Stelle — sie tragen keine eigene Logik, nur die
 * Verdrahtung. Steht getrennt, damit die Seite ihre Informationshierarchie zeigt statt einer
 * Liste von Dialogen (§4 der Code-Guidelines: eine Datei, ein Grund zu ändern).
 */
export function UserDetailDialogs({ user, access, state, onChange, mutations }: Props) {
  const { t } = useTranslation('accountRetention');
  const { deactivate, rename, resetTwoFactor, updateAccess } = mutations;
  return (
    <>
      <UserNameDialog
        open={state.name}
        onOpenChange={(open) => onChange({ name: open })}
        email={user.email}
        firstName={user.firstName ?? ''}
        lastName={user.lastName ?? ''}
        loading={rename.isPending}
        onSubmit={(name) => rename.mutate({ id: user.id, ...name })}
      />

      {access && (
        <UserAccessDialog
          open={state.access}
          onOpenChange={(open) => onChange({ access: open })}
          userLabel={fullName(user)}
          access={access}
          loading={updateAccess.isPending}
          onSubmit={(request) => updateAccess.mutate({ id: user.id, request })}
        />
      )}

      <UserTwoFactorResetDialog
        open={state.twoFactorReset}
        onOpenChange={(open) => onChange({ twoFactorReset: open })}
        userLabel={fullName(user)}
        loading={resetTwoFactor.isPending}
        onSubmit={({ password, code }) => resetTwoFactor.mutate({ id: user.id, password, code })}
      />

      <ConfirmDialog
        open={state.deactivate}
        onOpenChange={(open) => onChange({ deactivate: open })}
        title={`Zugang von ${fullName(user)} sperren?`}
        description={t('deactivateDescription')}
        confirmLabel="Zugang sperren"
        loading={deactivate.isPending}
        onConfirm={async () => {
          await deactivate.mutateAsync(user.id);
        }}
      />
    </>
  );
}
